import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Package, Undo2 } from "lucide-react";

const isYes = (v) => v === true || ["yes", "true", "1", "on"].includes(String(v ?? "").toLowerCase());

// Single-value control used inside per-product rows.
function CellInput({ type, options, value, onChange, disabled, testid }) {
  if (type === "toggle") {
    return (
      <div className="flex items-center gap-2">
        <Switch checked={isYes(value)} disabled={disabled} onCheckedChange={(v) => onChange(v ? "Yes" : "No")} data-testid={testid} />
        <span className={`text-xs font-medium ${isYes(value) ? "text-emerald-600" : value ? "text-rose-600" : "text-slate-400"}`}>{value === undefined || value === "" ? "Not set" : isYes(value) ? "Yes" : "No"}</span>
      </div>
    );
  }
  if (type === "checkbox") return <input type="checkbox" checked={!!value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="accent-indigo-600 w-4 h-4" data-testid={testid} />;
  if (type === "dropdown" || type === "radio") {
    return (
      <select value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm" data-testid={testid}>
        <option value="">Select…</option>
        {(options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return <Input type={type === "number" ? "number" : type === "date" ? "date" : "text"} value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="h-8 text-sm" data-testid={testid} />;
}

// Answer Per Product: the question repeats once for each product line on the job.
// Answer shape: [{line, product, sku, quantity, value}]
export function PerProductField({ field, value, onChange, disabled, taskId }) {
  const [lines, setLines] = useState(null);
  const rows = Array.isArray(value) ? value : [];
  useEffect(() => {
    if (disabled || !taskId) return;
    api.get(`/forms/job-products/${taskId}`).then((r) => setLines(r.data.lines || [])).catch(() => setLines([]));
  }, [taskId, disabled]);

  const list = disabled ? rows : (lines || []);
  const valueOf = (ln) => rows.find((r) => r.line === ln.line)?.value;
  const setLine = (ln, v) => {
    const rest = rows.filter((r) => r.line !== ln.line);
    onChange([...rest, { line: ln.line, product: ln.product, sku: ln.sku, quantity: ln.quantity, value: v }].sort((a, b) => a.line - b.line));
  };

  if (!disabled && !taskId) return <div className="text-xs text-amber-700 bg-amber-50 rounded-md px-2 py-1.5">Products are listed when this form is filled on a job.</div>;
  if (!disabled && lines === null) return <div className="text-xs text-slate-400">Loading job products…</div>;
  if (!list.length) return <div className="text-xs text-slate-400" data-testid={`per-product-empty-${field.id}`}>{disabled ? "No product answers" : "No products assigned to this job"}</div>;
  return (
    <div className="rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid={`per-product-${field.id}`}>
      {list.map((ln) => (
        <div key={ln.line} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`per-product-row-${field.id}-${ln.line}`}>
          <div className="min-w-0 flex items-center gap-2">
            <Package className="w-4 h-4 text-slate-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm text-slate-800 truncate">{ln.product || "Unnamed product"}</div>
              <div className="text-[11px] text-slate-400">Qty {ln.quantity ?? "—"}{ln.sku ? ` · ${ln.sku}` : ""}{ln.returned ? " · already returned" : ""}</div>
            </div>
          </div>
          <div className="shrink-0 w-40 flex justify-end">
            <CellInput type={field.type} options={field.options} disabled={disabled || ln.returned} value={disabled ? ln.value : valueOf(ln)}
              onChange={(v) => setLine(ln, v)} testid={`per-product-input-${field.id}-${ln.line}`} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ProductSearch({ onPick, testid }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!q.trim()) { setItems([]); return; }
    const t = setTimeout(() => api.get("/forms/product-lookup", { params: { q } }).then((r) => setItems(r.data.items || [])).catch(() => setItems([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative flex-1 min-w-0">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product or SKU…" className="h-8 text-sm" data-testid={testid} />
      {items.length > 0 && (
        <div className="absolute z-10 mt-1 w-full max-h-52 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {items.map((p) => (
            <button key={p.legacy_id} type="button" onClick={() => { onPick(p); setQ(""); setItems([]); }}
              className="block w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50" data-testid={`${testid}-opt-${p.legacy_id}`}>
              {p.title}{p.sku && <span className="text-[11px] text-slate-400 ml-1">{p.sku}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Free repeater: engineer adds product + quantity rows. Answer: [{product_legacy_id, title, sku, quantity}]
export function ProductRepeaterField({ field, value, onChange, disabled }) {
  const rows = Array.isArray(value) ? value : [];
  const upd = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-2" data-testid={`repeater-${field.id}`}>
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-200 px-2 py-1.5" data-testid={`repeater-row-${field.id}-${i}`}>
          {r.product_legacy_id
            ? <div className="flex-1 min-w-0 text-sm text-slate-800 truncate">{r.title}{r.sku && <span className="text-[11px] text-slate-400 ml-1">{r.sku}</span>}</div>
            : <ProductSearch onPick={(p) => upd(i, { product_legacy_id: p.legacy_id, title: p.title, sku: p.sku })} testid={`repeater-search-${field.id}-${i}`} />}
          <Input type="number" min="1" value={r.quantity ?? 1} disabled={disabled} onChange={(e) => upd(i, { quantity: Number(e.target.value) })}
            className="h-8 w-20 text-sm" data-testid={`repeater-qty-${field.id}-${i}`} />
          {!disabled && <button type="button" onClick={() => onChange(rows.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-rose-600" data-testid={`repeater-remove-${field.id}-${i}`}><Trash2 className="w-4 h-4" /></button>}
        </div>
      ))}
      {disabled && !rows.length && <div className="text-xs text-slate-400">No products added</div>}
      {!disabled && (
        <Button type="button" size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => onChange([...rows, { quantity: 1 }])} data-testid={`repeater-add-${field.id}`}>
          <Plus className="w-3.5 h-3.5" /> Add product
        </Button>
      )}
    </div>
  );
}

export function ActionsApplied({ items }) {
  if (!items?.length) return null;
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="submission-actions-applied">
      <div className="flex items-center gap-1.5 font-medium text-emerald-800 mb-1"><Undo2 className="w-4 h-4" /> Returned to warehouse</div>
      {items.map((a, i) => (
        <div key={i} className="text-xs text-emerald-700">{a.product} {a.quantity ? `× ${a.quantity}` : ""} · {a.status === "already_returned" ? "already returned" : a.restocked ? "stock restored" : "marked returned"}</div>
      ))}
    </div>
  );
}
